/**
 * Servicio Unificado para Búsqueda de Clientes
 * Usa el endpoint /wp-json/datofy/v1/users con búsqueda mejorada para todos los casos
 */

import React from 'react';
import { getSiteConfig } from 'utils/site';

/**
 * Obtener usuarios recientes sin filtros de búsqueda
 * @param {number} limit - Límite de resultados (default: 10, max: 50)
 * @returns {Promise<Array>} Array de usuarios recientes
 */
export const getRecentUsers = async (limit = 10) => {
  try {
    const searchLimit = Math.min(50, Math.max(5, parseInt(limit) || 10));
    console.log(`👥 UNIFIED SEARCH: Obteniendo ${searchLimit} usuarios recientes`);

    // Obtener configuración del sitio
    const config = JSON.parse(getSiteConfig());
    const baseURL = config.configs.find(c => c.key === 'WP_API_URL')?.value;
    
    if (!baseURL) {
      throw new Error('WP_API_URL no configurada');
    }

    // Construir URL del endpoint unificado sin parámetro search
    const usersURL = `${baseURL}/datofy/v1/users`;
    
    // Parámetros solo de paginación (sin search)
    const params = new URLSearchParams({
      per_page: searchLimit.toString(),
      page: '1'
    });

    const fullURL = `${usersURL}?${params}`;
    console.log('👥 UNIFIED SEARCH: URL para usuarios recientes:', fullURL);

    // Hacer la petición
    const response = await fetch(fullURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    // El endpoint /users devuelve un array directo de usuarios
    if (data && Array.isArray(data)) {
      const users = data;
      console.log(`✅ UNIFIED SEARCH: Obtenidos ${users.length} usuarios recientes`);
      
      // DEBUG: Verificar datos crudos del endpoint para usuarios recientes
      console.log('👥 UNIFIED SEARCH: Primeros 3 usuarios recientes crudos:', users.slice(0, 3).map(u => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name,
        last_name: u.last_name,
        email: u.email,
        group_name: u.group_name,
        role: u.role
      })));
      
      // Normalizar formato para compatibilidad con todos los componentes
      const normalizedUsers = users.map(user => {
        // Construir nombre completo
        const fullName = user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`.trim()
          : (user.display_name || 'Sin nombre');

        return {
          id: user.id,
          email: user.email,
          name: fullName,
          display_text: `${fullName} (${user.email})`,
          group: user.group_name || '',
          subscription: user.suscription_name || '',
          // Campos para compatibilidad con componentes
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          username: user.username || '',  // ✅ RUT del usuario
          role: user.role || '',
          // Estructura meta_data para compatibilidad con tabla Customers.js
          meta_data: [
            ...(user.meta_data || []),
            { key: 'group_name', value: user.group_name || '' },
            { key: 'suscription_name', value: user.suscription_name || '' }
          ]
        };
      });

      // DEBUG: Verificar datos normalizados para usuarios recientes
      console.log('👥 UNIFIED SEARCH: Primeros 3 usuarios recientes normalizados:', normalizedUsers.slice(0, 3).map(u => ({
        id: u.id,
        first_name: u.first_name,
        last_name: u.last_name,
        username: u.username,
        email: u.email,
        name: u.name,
        role: u.role,
        group_meta: u.meta_data?.find(m => m.key === 'group_name')?.value
      })));

      return normalizedUsers;
    } else {
      console.warn('👥 UNIFIED SEARCH: Respuesta inesperada para usuarios recientes:', data);
      return [];
    }

  } catch (error) {
    console.error('❌ UNIFIED SEARCH: Error obteniendo usuarios recientes:', error);
    return [];
  }
};

/**
 * Buscar clientes usando el endpoint unificado
 * @param {string} searchTerm - Término de búsqueda (nombre, email, RUT)
 * @param {number} limit - Límite de resultados (default: 10, max: 50)
 * @returns {Promise<Array>} Array de clientes encontrados
 */
export const searchCustomers = async (searchTerm, limit = 10) => {
  try {
    // Validar parámetros
    if (!searchTerm || searchTerm.trim().length < 1) {
      console.log('🔍 UNIFIED SEARCH: Término muy corto o vacío');
      return [];
    }

    const trimmedTerm = searchTerm.trim();
    const searchLimit = Math.min(50, Math.max(5, parseInt(limit) || 10));

    console.log(`🔍 UNIFIED SEARCH: Buscando "${trimmedTerm}" con límite ${searchLimit}`);

    // Obtener configuración del sitio
    const config = JSON.parse(getSiteConfig());
    const baseURL = config.configs.find(c => c.key === 'WP_API_URL')?.value;
    
    if (!baseURL) {
      throw new Error('WP_API_URL no configurada');
    }

    // Construir URL del endpoint unificado
    const searchURL = `${baseURL}/datofy/v1/users`;
    
    // Parámetros de búsqueda para endpoint /users
    const searchParams = new URLSearchParams({
      search: trimmedTerm,
      per_page: searchLimit.toString(),
      page: '1'
    });

    const fullURL = `${searchURL}?${searchParams}`;
    console.log('🔍 UNIFIED SEARCH: URL completa:', fullURL);

    // Hacer la petición
    const response = await fetch(fullURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    // El endpoint /users devuelve un array directo de usuarios
    if (data && Array.isArray(data)) {
      const customers = data;
      console.log(`✅ UNIFIED SEARCH: Encontrados ${customers.length} clientes para "${trimmedTerm}"`);
      
      // DEBUG: Verificar datos crudos del endpoint
      console.log('🔍 UNIFIED SEARCH: Primeros 3 usuarios crudos del endpoint:', customers.slice(0, 3).map(c => ({
        id: c.id,
        username: c.username,
        first_name: c.first_name,
        last_name: c.last_name,
        email: c.email,
        group_name: c.group_name,
        role: c.role
      })));
      
      // Normalizar formato para compatibilidad con todos los componentes
      const normalizedCustomers = customers.map(customer => {
        // Construir nombre completo
        const fullName = customer.first_name && customer.last_name
          ? `${customer.first_name} ${customer.last_name}`.trim()
          : (customer.display_name || 'Sin nombre');

        return {
          id: customer.id,
          email: customer.email,
          name: fullName,
          display_text: `${fullName} (${customer.email})`,
          group: customer.group_name || '',
          subscription: customer.suscription_name || '',
          // Campos para compatibilidad con componentes
          first_name: customer.first_name || '',
          last_name: customer.last_name || '',
          username: customer.username || '',  // ✅ RUT del usuario
          role: customer.role || '',
          // Estructura meta_data para compatibilidad con tabla Customers.js
          meta_data: [
            ...(customer.meta_data || []),
            { key: 'group_name', value: customer.group_name || '' },
            { key: 'suscription_name', value: customer.suscription_name || '' }
          ]
        };
      });

      // DEBUG: Verificar datos normalizados
      console.log('🔍 UNIFIED SEARCH: Primeros 3 usuarios normalizados:', normalizedCustomers.slice(0, 3).map(c => ({
        id: c.id,
        first_name: c.first_name,
        last_name: c.last_name,
        username: c.username,
        email: c.email,
        name: c.name,
        role: c.role,
        group_meta: c.meta_data?.find(m => m.key === 'group_name')?.value
      })));

      return normalizedCustomers;
    } else {
      console.warn('🔍 UNIFIED SEARCH: Respuesta inesperada:', data);
      return [];
    }

  } catch (error) {
    console.error('❌ UNIFIED SEARCH: Error en búsqueda:', error);
    return [];
  }
};

/**
 * Crear función debounced para búsqueda
 * @param {Function} callback - Función a ejecutar
 * @param {number} delay - Retraso en ms (default: 500)
 * @returns {Function} Función debounced
 */
export const createDebouncedSearch = (callback, delay = 500) => {
  let timeoutId;
  
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => callback.apply(null, args), delay);
  };
};

/**
 * Hook personalizado para búsqueda de clientes con debounce
 * @param {number} delay - Retraso en ms (default: 500)
 * @returns {Object} { searchCustomers: Function, isSearching: boolean }
 */
export const useCustomerSearch = (delay = 500) => {
  const [isSearching, setIsSearching] = React.useState(false);
  
  const debouncedSearch = React.useCallback(
    createDebouncedSearch(async (searchTerm, limit, callback) => {
      setIsSearching(true);
      try {
        const results = await searchCustomers(searchTerm, limit);
        callback(results);
      } finally {
        setIsSearching(false);
      }
    }, delay),
    [delay]
  );

  const performSearch = React.useCallback((searchTerm, limit = 10, callback) => {
    if (searchTerm.trim().length < 1) {
      callback([]);
      return;
    }
    debouncedSearch(searchTerm, limit, callback);
  }, [debouncedSearch]);

  return {
    searchCustomers: performSearch,
    isSearching
  };
};

/**
 * Función adaptadora para DynamicPaginator
 * Convierte búsqueda unificada para compatibilidad con filtros existentes
 * @param {string} searchTerm - Término de búsqueda
 * @param {Function} onFilterChange - Callback para notificar cambio de filtro
 * @param {string} filterName - Nombre del filtro (ej: 'searchRaw')
 * @param {number} delay - Delay para debounce (default: 500ms)
 */
export const createUnifiedSearchForFilter = (onFilterChange, filterName = 'searchRaw', delay = 500) => {
  let timeoutId;
  
  return (searchTerm) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      // Para DynamicPaginator, simplemente notificar el cambio sin formatear
      // El componente padre (Suscriptions) manejará la búsqueda con getCustomers
      onFilterChange(filterName, searchTerm);
    }, delay);
  };
};

export default searchCustomers;